import { Photo } from "api/types/photo";
import { SiteSurvey } from "api/types/siteSurvey";
import { SiteSurveyUnit } from "api/types/siteSurveyUnit";
import { SiteSurveyUnitAttribute } from "api/types/siteSurveyUnitAttribute";
import { Attribute } from "api/types/attribute";
import { useFormikContext } from "formik";
import { useCallback } from "react";
import { saveSurveyApi } from "routes/survey/addEditSurvey/surveyForm/helpers/saveSurveyApi";
import { useRoutes } from "routes/useRoutes";
import { useToast } from "shared/toast/useToast";
import { useAbortController } from "shared/useAbortController";
import { getEmptyUnit } from "./getEmptyUnit";
import { SiteSurveyAttribute } from "api/types/siteSurveyAttribute";

export interface StatusType {
  action: "editUnit" | "addUnit" | "editSurvey";
  unitIndexToEdit: undefined | number;
}

export function useSurveyForm() {
  const abortController = useAbortController();
  const { goBack } = useRoutes();
  const {
    values: survey,
    isSubmitting,
    resetForm,
    setValues,
    setFieldValue,
    setSubmitting,
  } = useFormikContext<SiteSurvey>();
  const { success, clear } = useToast();

  const saveSurvey = useCallback(async () => {
    setSubmitting(true);
    const { data: newSurvey } = await saveSurveyApi(
      survey,
      abortController.signal
    );
    resetForm({ values: newSurvey });
    setSubmitting(false);
    return newSurvey;
  }, [abortController.signal, resetForm, setSubmitting, survey]);

  const handleSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        clear();
        setSubmitting(true);
        e.preventDefault();
        await saveSurvey();
        setSubmitting(false);
        goBack();
        success("Saved!!!", 50);
      } catch (e) {
        setSubmitting(false);
        throw e;
      }
    },
    [clear, goBack, saveSurvey, setSubmitting, success]
  );

  const onAttributeValueChange = useCallback(
    (attributeConfig: Attribute, attributeValueText: string) => {
      const matchingAttribute = tryGetMatchingAttribute(
        survey.attributes,
        attributeConfig
      );

      if (!matchingAttribute) {
        const newAttribute: SiteSurveyUnitAttribute = {
          attributeId: attributeConfig.id,
          attributeValue: attributeValueText,
          photo: null,
          id: null,
        };
        survey.attributes.push(newAttribute);
        setValues(survey);
      } else {
        matchingAttribute.attributeValue = attributeValueText;
        setValues(survey);
      }
    },
    [setValues, survey]
  );

  return {
    survey,
    saveSurvey,
    isSubmitting,
    handleSubmit,
    setValues,
    setFieldValue,
    onAttributeValueChange,
  };
}

export function useUnitsForm() {
  const { values: survey, setValues } = useFormikContext<SiteSurvey>();

  const onDeleteUnit = useCallback(
    (unitIndexToDelete: number) => {
      const newUnits = [...survey.units];
      newUnits.splice(unitIndexToDelete, 1);
      survey.units = newUnits;
      setValues(survey);
    },
    [setValues, survey]
  );

  const onPhotoChange = useCallback(
    (unit: SiteSurveyUnit, photo: Photo, attributeConfig: Attribute) => {
      const matchingAttribute = tryGetMatchingAttribute(
        unit.attributes,
        attributeConfig
      );

      if (!matchingAttribute) {
        const newAttribute: SiteSurveyUnitAttribute = {
          attributeId: attributeConfig.id,
          attributeValue: "",
          id: null,
          photo: { ...photo },
        };
        unit.attributes.push(newAttribute);
      } else {
        matchingAttribute.photo = { ...photo };
      }
      setValues(survey);
    },
    [setValues, survey]
  );

  const onAttributeValueChange = useCallback(
    (
      unit: SiteSurveyUnit,
      attributeConfig: Attribute,
      attributeValueText: string
    ) => {
      const matchingAttribute = tryGetMatchingAttribute(
        unit.attributes,
        attributeConfig
      );

      if (!matchingAttribute) {
        const newAttribute: SiteSurveyUnitAttribute = {
          attributeId: attributeConfig.id,
          attributeValue: attributeValueText,
          photo: null,
          id: null,
        };
        unit.attributes.push(newAttribute);
        setValues(survey);
      } else {
        matchingAttribute.attributeValue = attributeValueText;
        setValues(survey);
      }
    },
    [setValues, survey]
  );

  const onDeletePhoto = useCallback(
    (unit: SiteSurveyUnit, attributeConfig: Attribute) => {
      const matchingAttribute = tryGetMatchingAttribute(
        unit.attributes,
        attributeConfig
      );

      if (!matchingAttribute) {
        const newAttribute: SiteSurveyUnitAttribute = {
          attributeId: attributeConfig.id,
          attributeValue: "",
          id: null,
          photo: null,
        };
        unit.attributes.push(newAttribute);
      } else {
        matchingAttribute.photo = null;
      }
      setValues(survey);
    },
    [setValues, survey]
  );

  const addNewUnit = useCallback(() => {
    const emptyUnit = getEmptyUnit(survey);
    survey.units.push(emptyUnit);
    setValues(survey);
  }, [setValues, survey]);

  return {
    onAttributeValueChange,
    onPhotoChange,
    onDeletePhoto,
    addNewUnit,
    onDeleteUnit,
  };
}

function tryGetMatchingAttribute(
  attributes: SiteSurveyAttribute[],
  attributeConfig: Attribute
) {
  const matchingAttribute = attributes.find(
    (att) => att.attributeId === attributeConfig.id
  );

  return matchingAttribute;
}
